/**
 * A tag-style cell editor for entering multiple string values.
 * Stores values as a string[] array.
 * Users can type values and press comma/semicolon/enter to add tags.
 * Optionally shows autocomplete suggestions from the company text field options API.
 */
import { forwardRef, useImperativeHandle, useState, useRef } from "react"
import { ICellEditorParams } from "ag-grid-community"
import StringTagInput from "../../string-tag-input"
import type { tStringTagInputHandle } from "../../string-tag-input"

/**
 * Extended props for the StringTagCellEditor.
 * Includes optional suggestion source params passed via cellEditorParams.
 */
type tStringTagCellEditorParams = ICellEditorParams & {
    /** The model name for the companyTextFieldOptions search (e.g. "Employee", "Equipment") */
    suggestionModel?: string
    /** The field name for the companyTextFieldOptions search (e.g. "department") */
    suggestionFieldName?: string
}

/**
 * Parse the input value into a string array.
 * Handles both string[] and null/undefined inputs.
 */
const parseValues = (value: unknown): string[] => {
    if (!value) return []
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === "string" && item.trim() !== "")
    }
    return []
}

/**
 * AG-Grid cell editor for entering multiple string values as tags.
 * Values are stored as a string[] array.
 * When suggestionModel and suggestionFieldName are provided, fetches distinct custom
 * field values from the API and shows them as autocomplete suggestions.
 *
 * New implementation: delegates UI and interaction to the reusable StringTagInput
 * component. This editor is a thin AG-Grid adapter that manages the tag values
 * and exposes AG-Grid `getValue` / `afterGuiAttached` via ref.
 */
const StringTagCellEditor = forwardRef(
    (props: tStringTagCellEditorParams, ref): JSX.Element => {
        const [values, setValues] = useState<string[]>(parseValues(props.value))
        const inputHandleRef = useRef<tStringTagInputHandle>(null)

        useImperativeHandle(ref, () => ({
            /** Return committed values plus any pending in-flight input text. */
            getValue: () => inputHandleRef.current?.getValueWithPending() ?? values,
            afterGuiAttached: () => {
                setTimeout(() => {
                    inputHandleRef.current?.focus()
                }, 0)
            },
        }))

        return <StringTagInput ref={inputHandleRef} values={values} onChange={setValues} suggestionModel={props.suggestionModel} suggestionFieldName={props.suggestionFieldName} />
    }
)

export default StringTagCellEditor
